import styled from 'styled-components';

import { PLAYERS, MOBILE, ANIM, GRADIENTS, COLORS } from '../../../../theme/tokens';
import { Text } from '../../../../components';

// The filter tray body. It renders INSIDE the sticky header block (see
// `players.styles` → FilterTray), so it carries no surface of its own: a panel
// inside the pinned pill would read as a card floating in a card. A top hairline
// divides it from the title row instead, so header + filters read as one bar
// whether the header is pinned or not.
export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* Top padding is the gap to the title row; it collapses with the tray. */
  padding: 16px 0 2px;
  margin-top: 14px;
  border-top: 1px solid ${PLAYERS.hairline};
  border-image: ${GRADIENTS.dividerStyledH} 1;
`;

// Auto-fitting columns: four across on desktop, two on a tablet, one on a phone —
// without a media-query per breakpoint.
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;

  ${MOBILE} {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;

export const FieldRoot = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const FieldLabel = styled(Text)`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${PLAYERS.headerLabel};
`;

// Same pill surface as the header's sort control, so the tray reads as one family.
export const FieldControl = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border-radius: 12px;
  background: ${PLAYERS.searchBg};
  border: 1px solid ${PLAYERS.searchBorder};
  cursor: pointer;
  transition: border-color ${ANIM.INTERACT_MS}ms ease;

  &:focus-within {
    border-color: ${PLAYERS.searchFocusBorder};
  }
`;

export const FieldValue = styled.span`
  flex: 1;
  min-width: 0;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  color: ${PLAYERS.searchText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
`;

export const FieldChevron = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  pointer-events: none;
  color: ${PLAYERS.searchIcon};
`;

// An invisible full-box overlay so a click anywhere on the pill opens the native
// picker (and mobile gets the OS wheel) — same trick as the header's sort select.
export const FieldSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  border: none;
  outline: none;
  opacity: 0;
  /* Invisible, but Windows/Linux paint the OPEN list from the select's own colours:
     transparent + inherited white text came out white on white. Solid themed colours
     give a readable dark list (macOS uses its own picker and ignores this). */
  background: ${PLAYERS.searchBg};
  color: ${PLAYERS.searchText};
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;

  option {
    background: ${PLAYERS.searchBg};
    color: ${PLAYERS.searchText};
  }
`;

// Result count + clear action under the selects. No divider here: the tray already
// has one above it, and a second hairline inside a pinned bar reads as clutter.
export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const Summary = styled(Text)`
  font-size: 12px;
  color: ${PLAYERS.pageMeta};
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 99px;
  border: 1px solid ${PLAYERS.pageBorder};
  background: transparent;
  color: ${PLAYERS.pageText};
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background ${ANIM.INTERACT_MS}ms ease, color ${ANIM.INTERACT_MS}ms ease;

  &:hover {
    background: ${PLAYERS.pageHoverBg};
    color: ${COLORS.text};
  }
`;
